<template>
  <card-dialog
    v-model="modalVisible"
    max-width="500px"
    :title="membershipPlanFormData.id ? $t('card_title.edit_membership_plan') : $t('card_title.add_membership_plan') "
  >
    <template v-slot:title>
      <v-card-title class="grey-bg px-6 py-3">
        <div v-if="membershipPlan">
          {{ $t('card_title.edit_membership_plan') }}
        </div>
        <div v-else>
          {{ $t('new_membership_plan.dialog.header') }}
        </div>
      </v-card-title>
    </template>
    <v-card-text
      class="pa-6"
    >
      <validation-observer ref="validation">
        <validation-provider
          v-slot="{ errors }"
          rules="required"
        >
          <v-text-field
            v-model="membershipPlanFormData.title"
            :error-messages="errors"
            :label="$t('text_input.title.title')"
            dense
            outlined
            :placeholder="$t('text_input.new_membership.title.placeholder')"
            class="mb-4"
          />
        </validation-provider>

        <validation-provider
          v-slot="{ errors }"
          rules="required"
        >
          <v-select
            v-model="membershipPlanFormData.interval"
            :error-messages="errors"
            :label="$t('membership.payment_intervals.label')"
            dense
            :items="membershipPlanIntervals"
            item-value="key"
            item-text="text"
            outlined
          />
        </validation-provider>

        <validation-provider
          v-slot="{ errors }"
          rules="required"
        >
          <currency-input
            v-model="membershipPlanFormData.price"
            :error-messages="errors"
            :label="$t('text_input.monthly_price.title')"
            dense
            outlined
            :options="{ currency: currency, precision: 0 }"
            :placeholder="$t('membership_price.placeholder')"
          />
        </validation-provider>
      </validation-observer>
    </v-card-text>
    <template v-slot:actions>
      <v-card-actions class="pa-4">
        <div class="flex-grow-1">
          <v-btn
            color="primary"
            block
            large
            :loading="submitting"
            @click="submit()"
          >
            {{ $t('training_builder.button.save') }}
          </v-btn>
          <v-btn
            v-if="membershipPlanFormData.id"
            color="error"
            text
            block
            class="mt-1"
            @click="toggleConfirmationDialog(true)"
          >
            {{ $t('button.delete') }}
          </v-btn>
        </div>
      </v-card-actions>
    </template>

    <delete-confirmation-dialog
      :visible.sync="deleteConfirmationDialogVisible"
      :submitting="deleting"
      @confirmed="deleteMembershipPlan()"
    />
  </card-dialog>
</template>

<script>
import CardDialog from '@/components/helpers/CardDialog'
import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
import { MembershipPlanApiService } from '@/data/services/MembershipPlanApiService'
import { EventBus } from '@/plugins/bus'
import DeleteConfirmationDialog from '@/components/dialogs/DeleteConfirmationDialog'
import CurrencyInput from '@/components/helpers/CurrencyInput'
import { cloneDeep } from 'lodash'
import { MembershipPlanIntervals } from '@/data/models/MembershipPlan'

const emptyMembershipPlanFormData = {
  id: undefined,
  title: '',
  price: 0,
  interval: 'monthly'
}

export default {
  name: 'MembershipPlanFormDialog',
  components: { CurrencyInput, DeleteConfirmationDialog, CardDialog },
  mixins: [deleteConfirmationMixin],
  props: {
    membershipPlan: {
      type: Object,
      default: undefined
    },
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      membershipPlanFormData: emptyMembershipPlanFormData,
      submitting: false
    }
  },
  computed: {
    modalVisible: {
      get: function () {
        return this.visible
      },
      set: function (value) {
        this.$emit('update:visible', value)
      }
    },
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    currency() {
      return this.user.organisation_settings.currency || 'EUR'
    }
  },
  created() {
    this.membershipPlanIntervals = Object.keys(MembershipPlanIntervals).map(k => ({ key: k, ...MembershipPlanIntervals[k] }))

    EventBus.$on(this.$events.OPEN_DIALOG_MEMBERSHIP_PLAN_FORM, () => {
      this.modalVisible = true
      this.membershipPlanFormData = cloneDeep(emptyMembershipPlanFormData)
    })

    EventBus.$on(this.$events.EDIT_MEMBERSHIP_PLAN, (membershipPlan) => {
      this.modalVisible = true

      this.membershipPlanFormData = {
        id: membershipPlan.id,
        title: membershipPlan.title,
        price: membershipPlan.price,
        currency: membershipPlan.currency,
        interval: membershipPlan.interval
      }
    })
  },
  methods: {
    async submit() {
      if (!(await this.$refs.validation.validate())) return

      await this.save()
    },
    async save() {
      try {
        this.submitting = true

        const data = {
          title: this.membershipPlanFormData.title,
          price: this.membershipPlanFormData.price,
          currency: this.membershipPlanFormData.currency,
          interval: this.membershipPlanFormData.interval
        }

        let result

        if (this.membershipPlanFormData.id) {
          data.id = this.membershipPlanFormData.id

          result = await MembershipPlanApiService.update(data)
        } else {
          result = await MembershipPlanApiService.create(data)
        }

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.membership_plan_save.success'),
          color: 'success'
        })

        this.$emit('refresh', result.data)

        if (this.membershipPlanFormData.id) {
          EventBus.$emit(this.$events.MEMBERSHIP_PLAN_UPDATED)
        } else {
          EventBus.$emit(this.$events.MEMBERSHIP_PLAN_CREATED)
        }

        this.modalVisible = false
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.membership_plan_save.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.submitting = false
      }
    },
    async deleteMembershipPlan() {
      try {
        this.deleting = true

        await MembershipPlanApiService.delete(this.membershipPlanFormData.id)

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.membership_plan_delete.success'),
          color: 'success'
        })

        this.deleteConfirmationDialogVisible = false

        EventBus.$emit(this.$events.MEMBERSHIP_PLAN_DELETED, this.membershipPlanFormData)
        this.modalVisible = false
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.membership_plan_delete.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.deleting = false
      }
    }
  }
}
</script>
